import { useCallback, useEffect, useRef, useState } from "react";
import { WINDOW_BY_ID, WINDOW_IDS, WINDOWS, MOBILE_WINDOW_MARGIN } from "../data/windows";
import {
  clampPosition,
  clampResize,
  createWindowState,
  fitRectToDesktop,
  fitWindowToViewport,
  getDefaultRect,
  getFocusedId,
  getMaximizedRect,
  isMobileViewport,
  nextZIndex,
} from "../utils/windowGeometry";

const getInitialStatus = (window) => {
  if (window.mobileOnly) {
    return isMobileViewport() ? "open" : "closed";
  }

  return (
    window.initialStatus ??
    (window.id === WINDOW_IDS.BIO ? "open" : "minimized")
  );
};

const createInitialWindows = () =>
  WINDOWS.reduce((acc, window, index) => {
    acc[window.id] = createWindowState(
      window.id,
      getInitialStatus(window),
      index + 1
    );
    return acc;
  }, {});

const focusWindow = (windows, id) => {
  const win = windows[id];

  if (!win || win.status !== "open") {
    return windows;
  }

  const focusedId = getFocusedId(windows);

  if (focusedId === id) {
    return windows;
  }

  return {
    ...windows,
    [id]: { ...win, zIndex: nextZIndex(windows) },
  };
};

const openWindow = (windows, id) => {
  if (WINDOW_BY_ID[id]?.mobileOnly && !isMobileViewport()) {
    return windows;
  }

  const current = windows[id] || createWindowState(id, "closed", 1);
  let next = { ...current, status: "open" };

  if (current.status === "closed") {
    next = {
      ...next,
      ...(current.width && current.height
        ? {
            x: current.x,
            y: current.y,
            width: current.width,
            height: current.height,
            isMaximized: current.isMaximized,
            restoreRect: current.restoreRect,
          }
        : getDefaultRect(id)),
    };
  }

  return focusWindow({ ...windows, [id]: next }, id);
};

export const useWindows = () => {
  const [windows, setWindows] = useState(createInitialWindows);
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const wasMobileRef = useRef(isMobileViewport());

  useEffect(() => {
    let frame = 0;

    const applyViewportFit = () => {
      const mobile = isMobileViewport();
      const enteredMobile = mobile && !wasMobileRef.current;
      const leftMobile = !mobile && wasMobileRef.current;
      wasMobileRef.current = mobile;
      setIsMobile(mobile);
      setWindows((prev) => {
        let changed = false;
        const next = { ...prev };

        Object.keys(next).forEach((id) => {
          const meta = WINDOW_BY_ID[id];
          let win = next[id];

          if (meta?.mobileOnly) {
            if (leftMobile && win.status !== "closed") {
              win = { ...win, status: "closed" };
              changed = true;
            } else if (enteredMobile) {
              win = {
                ...win,
                status: "open",
                ...getDefaultRect(id),
                isMaximized: false,
                restoreRect: null,
              };
              changed = true;
            }

            next[id] = win;
          }

          const fitted = fitWindowToViewport(next[id]);

          if (
            fitted.x !== next[id].x ||
            fitted.y !== next[id].y ||
            fitted.width !== next[id].width ||
            fitted.height !== next[id].height ||
            fitted.isMaximized !== next[id].isMaximized
          ) {
            next[id] = fitted;
            changed = true;
          }
        });

        if (enteredMobile && next[WINDOW_IDS.NOTICE]) {
          next[WINDOW_IDS.NOTICE] = {
            ...next[WINDOW_IDS.NOTICE],
            zIndex: nextZIndex(next),
          };
          changed = true;
        }

        return changed ? next : prev;
      });
    };

    const onResize = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        applyViewportFit();
      });
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const open = useCallback((id) => {
    setWindows((prev) => openWindow(prev, id));
  }, []);

  const focus = useCallback((id) => {
    setWindows((prev) => focusWindow(prev, id));
  }, []);

  const minimize = useCallback((id) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win || win.status !== "open") {
        return prev;
      }

      return { ...prev, [id]: { ...win, status: "minimized" } };
    });
  }, []);

  const close = useCallback((id) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win || win.status === "closed") {
        return prev;
      }

      return { ...prev, [id]: { ...win, status: "closed" } };
    });
  }, []);

  const toggleMaximize = useCallback((id) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win || win.status !== "open") {
        return prev;
      }

      const margin = isMobileViewport() ? MOBILE_WINDOW_MARGIN : 0;

      if (win.isMaximized) {
        const rect = fitRectToDesktop(
          win.restoreRect || getDefaultRect(id),
          margin
        );

        return {
          ...prev,
          [id]: {
            ...win,
            ...rect,
            isMaximized: false,
            restoreRect: null,
            zIndex: nextZIndex(prev),
          },
        };
      }

      return {
        ...prev,
        [id]: {
          ...win,
          restoreRect: {
            x: win.x,
            y: win.y,
            width: win.width,
            height: win.height,
          },
          ...getMaximizedRect(),
          isMaximized: true,
          zIndex: nextZIndex(prev),
        },
      };
    });
  }, []);

  const move = useCallback((id, x, y, size) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win) {
        return prev;
      }

      const width = size?.width ?? win.width;
      const height = size?.height ?? win.height;
      const position = clampPosition(x, y, width, height);

      return {
        ...prev,
        [id]: {
          ...win,
          ...position,
          width,
          height,
          isMaximized: false,
          restoreRect: null,
        },
      };
    });
  }, []);

  const resize = useCallback((id, proposed, edge) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win || win.isMaximized) {
        return prev;
      }

      const origin = {
        x: win.x,
        y: win.y,
        width: win.width,
        height: win.height,
      };

      return {
        ...prev,
        [id]: {
          ...win,
          ...clampResize(origin, proposed, edge),
          isMaximized: false,
        },
      };
    });
  }, []);

  const toggleTaskbar = useCallback((id) => {
    setWindows((prev) => {
      const win = prev[id];

      if (!win) {
        return prev;
      }

      if (win.status === "open") {
        if (getFocusedId(prev) === id) {
          return { ...prev, [id]: { ...win, status: "minimized" } };
        }

        return focusWindow(prev, id);
      }

      return openWindow(prev, id);
    });
  }, []);

  const isOpen = useCallback((id) => windows[id]?.status === "open", [windows]);
  const isFocused = useCallback(
    (id) => getFocusedId(windows) === id,
    [windows]
  );
  const isOnTaskbar = useCallback(
    (id) => windows[id]?.status !== "closed",
    [windows]
  );
  const isMounted = useCallback(
    (id) => windows[id]?.status !== "closed",
    [windows]
  );

  return {
    windows,
    isMobile,
    open,
    focus,
    minimize,
    close,
    toggleMaximize,
    move,
    resize,
    toggleTaskbar,
    isOpen,
    isFocused,
    isOnTaskbar,
    isMounted,
  };
};
